import { PeriodType } from 'typed/api/enumerations'

const DAY_MS: number = 24 * 60 * 60 * 1000;
const WEEK_MS: number = 7 * DAY_MS;

export { PeriodType };

export type Dateable = Date | number;

export const equals = (left: Period, right: Period) => left.type === right.type && +left === +right;

export default class Period extends Date {
    static readonly equals = equals;

    _align(): void {
        if (!this._isLocal) {
            super.setUTCHours(0, 0, 0, 0);
        }
        else {
            super.setHours(0, 0, 0, 0);
        }

        switch (this._type) {
            case PeriodType.DAY:
                break;

            case PeriodType.WEEK:
                if (!this._isLocal) {
                    super.setUTCDate(super.getUTCDate() - (super.getUTCDay() || 7) + 1);
                }
                else {
                    super.setDate(super.getDate() - (super.getDay() || 7) + 1);
                }
                break;

            case PeriodType.MONTH:
                if (!this._isLocal) {
                    super.setUTCDate(1);
                }
                else {
                    super.setDate(1);
                }
                break;

            case PeriodType.QUARTER:
                if (!this._isLocal) {
                    super.setUTCDate(1);
                    super.setUTCQuarter(super.getUTCQuarter());
                }
                else {
                    super.setDate(1);
                    super.setQuarter(super.getQuarter());
                }
                break;

            case PeriodType.YEAR:
                if (!this._isLocal) {
                    super.setUTCDate(1);
                    super.setUTCMonth(0);
                }
                else {
                    super.setDate(1);
                    super.setMonth(0);
                }
                break;

            default:
                throw new Error(`Unknown PeriodType: ${this._type}.`);
        }
    }

    private _type: PeriodType;
    private _isLocal: boolean;

    constructor(period: Period);
    constructor(type: PeriodType, date?: Dateable | null, isLocal?: boolean)
    constructor(...args: any[]) {
        const type = args[0] instanceof Period ? args[0].type : args[0] || PeriodType.MONTH;
        const isLocal = args[0] instanceof Period ? args[0].isLocal : args[2] || false;

        let date = args[0] instanceof Period ? args[0] : args[1] || new Date(); 
        const isValidDate = date !== null && !isNaN(date);

        if (!isValidDate) {
            date = new Date();
        }

        super(date.valueOf() as number);

        this._type = type;
        this._isLocal = isLocal;

        if (!(date instanceof Period && date.type >= type)) {
            this._align();
        }
    }

    get type(): PeriodType {
        return this._type;
    }

    get isLocal(): boolean {
        return this._isLocal;
    }

    get previous(): Period {
        return this.add(-1);
    }

    get start(): Date {
        return new Date(this.valueOf() as number);
    }
    get end(): Date {
        return new Date(this.add(1).valueOf() - 1000);
    }

    get next(): Period {
        return this.add(1);
    }

    add(periodCount: number): Period {
        switch (this._type) {
            case PeriodType.DAY:
                return new Period(this).setUTCDate(this.getUTCDate() + periodCount, true);

            case PeriodType.WEEK:
                return new Period(this).setUTCDate(this.getUTCDate() + periodCount * 7, true);

            case PeriodType.MONTH:
                return new Period(this).setUTCMonth(this.getUTCMonth() + periodCount, true);

            case PeriodType.QUARTER:
                return new Period(this).setUTCQuarter(this.getUTCQuarter() + periodCount, true);

            case PeriodType.YEAR:
                return new Period(this).setUTCFullYear(this.getUTCFullYear() + periodCount, true);

            default:
                throw new Error(`Unknown PeriodType: ${this._type}.`);
        }
    }

    sub(other: Dateable): number {
        switch (this._type) {
            case PeriodType.DAY:
                return Math.floor(this.valueOf() - other.valueOf()) / DAY_MS;

            case PeriodType.WEEK:
                return Math.floor(this.valueOf() - other.valueOf()) / WEEK_MS;

            case PeriodType.MONTH:
                return (this.getUTCFullYear() - new Date(other).getUTCFullYear()) * 12 + (this.getUTCMonth() - new Date(other).getUTCMonth());

            case PeriodType.QUARTER:
                return (this.getUTCFullYear() - new Date(other).getUTCFullYear()) * 4 + (this.getUTCQuarter() - new Date(other).getUTCQuarter());

            case PeriodType.YEAR:
                return this.getUTCFullYear() - new Date(other).getUTCFullYear();

            default:
                throw new Error(`Unknown PeriodType: ${this._type}.`);
        }
    }

    setFullYear(value: number): number;
    setFullYear(value: number, asPeriod: boolean): Period;
    setFullYear(value: number, asPeriod: boolean = false): number | Period { if (super.getFullYear() !== value) { super.setFullYear(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setWeeklyYear(value: number): number;
    setWeeklyYear(value: number, asPeriod: boolean): Period;
    setWeeklyYear(value: number, asPeriod: boolean = false): number | Period { if (super.getWeeklyYear() !== value) { super.setWeeklyYear(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setQuarter(value: number): number;
    setQuarter(value: number, asPeriod: boolean): Period;
    setQuarter(value: number, asPeriod: boolean = false): number | Period { if (super.getQuarter() !== value) { super.setQuarter(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setMonth(value: number): number;
    setMonth(value: number, asPeriod: boolean): Period;
    setMonth(value: number, asPeriod: boolean = false): number | Period { if (super.getMonth() !== value) { super.setMonth(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setWeek(value: number): number;
    setWeek(value: number, asPeriod: boolean): Period;
    setWeek(value: number, asPeriod: boolean = false): number | Period { if (super.getWeek() !== value) { super.setWeek(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setDate(value: number): number;
    setDate(value: number, asPeriod: boolean): Period;
    setDate(value: number, asPeriod: boolean = false): number | Period { if (super.getDate() !== value) { super.setDate(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setHours(value: number): number;
    setHours(value: number, asPeriod: boolean): Period;
    setHours(value: number, asPeriod: boolean = false): number | Period { if (super.getHours() !== value) { super.setHours(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setMinutes(value: number): number;
    setMinutes(value: number, asPeriod: boolean): Period;
    setMinutes(value: number, asPeriod: boolean = false): number | Period { if (super.getMinutes() !== value) { super.setMinutes(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setSeconds(value: number): number;
    setSeconds(value: number, asPeriod: boolean): Period;
    setSeconds(value: number, asPeriod: boolean = false): number | Period { if (super.getSeconds() !== value) { super.setSeconds(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setMilliseconds(value: number): number;
    setMilliseconds(value: number, asPeriod: boolean): Period;
    setMilliseconds(value: number, asPeriod: boolean = false): number | Period { if (super.getMilliseconds() !== value) { super.setMilliseconds(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setTime(value: number): number;
    setTime(value: number, asPeriod: boolean): Period;
    setTime(value: number, asPeriod: boolean = false): number | Period { if (super.getTime() !== value) { super.setTime(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCFullYear(value: number): number;
    setUTCFullYear(value: number, asPeriod: boolean): Period;
    setUTCFullYear(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCFullYear() !== value) { super.setUTCFullYear(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCWeeklyYear(value: number): number;
    setUTCWeeklyYear(value: number, asPeriod: boolean): Period;
    setUTCWeeklyYear(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCWeeklyYear() !== value) { super.setUTCWeeklyYear(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCQuarter(value: number): number;
    setUTCQuarter(value: number, asPeriod: boolean): Period;
    setUTCQuarter(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCQuarter() !== value) { super.setUTCQuarter(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCMonth(value: number): number;
    setUTCMonth(value: number, asPeriod: boolean): Period;
    setUTCMonth(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCMonth() !== value) { super.setUTCMonth(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCWeek(value: number): number;
    setUTCWeek(value: number, asPeriod: boolean): Period;
    setUTCWeek(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCWeek() !== value) { super.setUTCWeek(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCDate(value: number): number;
    setUTCDate(value: number, asPeriod: boolean): Period;
    setUTCDate(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCDate() !== value) { super.setUTCDate(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCHours(value: number): number;
    setUTCHours(value: number, asPeriod: boolean): Period;
    setUTCHours(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCHours() !== value) { super.setUTCHours(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCMinutes(value: number): number;
    setUTCMinutes(value: number, asPeriod: boolean): Period;
    setUTCMinutes(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCMinutes() !== value) { super.setUTCMinutes(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCSeconds(value: number): number;
    setUTCSeconds(value: number, asPeriod: boolean): Period;
    setUTCSeconds(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCSeconds() !== value) { super.setUTCSeconds(value); this._align(); } return asPeriod ? this : this.valueOf(); }
    setUTCMilliseconds(value: number): number;
    setUTCMilliseconds(value: number, asPeriod: boolean): Period;
    setUTCMilliseconds(value: number, asPeriod: boolean = false): number | Period { if (super.getUTCMilliseconds() !== value) { super.setUTCMilliseconds(value); this._align(); } return asPeriod ? this : this.valueOf(); }
}